import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { api } from "../../api/client";
import { LoadingSection } from "../../components/Spinner";

type Granularity = "day" | "week" | "month";

type Stats = {
  range: RangeKey;
  granularity: Granularity;
  from: string;
  pageviews: number;
  leads: number;
  leadsUnread: number;
  resaClicks: number;
  interactionsTotal: number;
  recentLeads: Array<{ id: string; name: string; email: string; createdAt: string; subject?: string }>;
  eventsByDay: Array<{ day: string; type: string; count: number }>;
  topPages: Array<{ path: string; count: number }>;
  interactions: Array<{ type: string; count: number }>;
  eventsByGym: Array<{ gym: string; count: number }>;
  leadsByType: Array<{ type: string; count: number }>;
};

// Périodes d'affichage. Les données brutes sont conservées sans limite en base ;
// ce sélecteur ne fait que choisir la fenêtre agrégée envoyée par /admin/stats.
const RANGES = [
  ["7d", "7 jours"],
  ["30d", "30 jours"],
  ["12m", "12 mois"],
  ["24m", "24 mois"],
  ["all", "Tout"],
] as const;
type RangeKey = (typeof RANGES)[number][0];

const INTERACTION_LABELS: Record<string, string> = {
  reservation_click: "Ouverture page réservation",
  reservation_open: "Clic plateforme de réservation",
  phone_click: "Clic téléphone",
  email_click: "Clic email",
  directions_click: "Clic itinéraire",
  pricing_interest: "Intérêt formule (tarifs)",
};

const GYM_LABELS: Record<string, string> = {
  brest: "Brest",
  landerneau: "Landerneau",
};

const LEAD_TYPE_LABELS: Record<string, string> = {
  contact: "Demande d'information",
  trial: "Séance d'essai",
  dropin: "Drop-in",
};

export function AdminDashboard() {
  const [range, setRange] = useState<RangeKey>("30d");
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "stats", range],
    queryFn: () => api<Stats>(`/admin/stats?range=${range}`),
  });

  const rangeLabel = RANGES.find(([k]) => k === range)?.[1] ?? range;
  const chartData = aggregateByDay(data?.eventsByDay ?? [], data?.granularity ?? "day");

  if (isLoading) return <LoadingSection />;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl font-bold uppercase">Dashboard</h1>
        <div className="inline-flex rounded-md border border-ink-200 bg-white p-0.5">
          {RANGES.map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setRange(key)}
              className={`rounded px-3 py-1.5 text-xs font-semibold transition ${
                range === key ? "bg-brand-500 text-white" : "text-ink-500 hover:bg-ink-100"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Kpi label={`Vues (${rangeLabel})`} value={data?.pageviews ?? 0} />
        <Kpi
          label={`Messages (${rangeLabel})`}
          value={data?.leads ?? 0}
          hint={data?.leadsUnread ? `${data.leadsUnread} non lu${data.leadsUnread > 1 ? "s" : ""}` : undefined}
        />
        <Kpi label={`Ouverture page réservation (${rangeLabel})`} value={data?.resaClicks ?? 0} />
        <Kpi label={`Interactions (${rangeLabel})`} value={data?.interactionsTotal ?? 0} />
      </div>

      <div className="card">
        <h2 className="mb-4 font-display text-lg font-bold uppercase">Activité ({rangeLabel})</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" fontSize={12} />
              <YAxis yAxisId="left" fontSize={12} allowDecimals={false} />
              <YAxis yAxisId="right" orientation="right" fontSize={12} allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="pageview"
                name="Vues de page"
                stroke="#DC2626"
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="lead"
                name="Mails reçus"
                stroke="#0891B2"
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="reservation_click"
                name="Ouverture page réservation"
                stroke="#059669"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card">
          <h2 className="mb-4 font-display text-lg font-bold uppercase">Pages les plus vues ({rangeLabel})</h2>
          <BarList
            items={(data?.topPages ?? []).map((p) => ({ label: p.path, value: p.count }))}
            empty="Aucune vue enregistrée."
          />
        </div>

        <div className="card">
          <h2 className="mb-4 font-display text-lg font-bold uppercase">Interactions ({rangeLabel})</h2>
          <BarList
            items={(data?.interactions ?? [])
              .map((i) => ({ label: INTERACTION_LABELS[i.type] ?? i.type, value: i.count }))
              .sort((a, b) => b.value - a.value)}
            empty="Aucune interaction enregistrée."
          />
        </div>

        <div className="card">
          <h2 className="mb-4 font-display text-lg font-bold uppercase">Interactions par salle ({rangeLabel})</h2>
          <BarList
            items={(data?.eventsByGym ?? []).map((g) => ({
              label: GYM_LABELS[g.gym] ?? g.gym,
              value: g.count,
            }))}
            empty="Aucune donnée par salle."
          />
        </div>

        <div className="card">
          <h2 className="mb-4 font-display text-lg font-bold uppercase">Messages par type ({rangeLabel})</h2>
          <BarList
            items={(data?.leadsByType ?? []).map((l) => ({
              label: LEAD_TYPE_LABELS[l.type] ?? l.type,
              value: l.count,
            }))}
            empty="Aucun message."
          />
        </div>
      </div>

      <div className="card">
        <h2 className="mb-4 font-display text-lg font-bold uppercase">Derniers messages</h2>
        <ul className="divide-y divide-ink-100">
          {data?.recentLeads.map((l) => (
            <li key={l.id} className="flex items-center justify-between py-3 text-sm">
              <div>
                <div className="font-semibold">{l.name}</div>
                <div className="text-xs text-ink-500">{l.email}</div>
              </div>
              <div className="text-xs text-ink-500">
                {new Date(l.createdAt).toLocaleString("fr-FR")}
              </div>
            </li>
          ))}
          {data?.recentLeads.length === 0 && (
            <p className="py-6 text-center text-sm text-ink-500">Aucun message.</p>
          )}
        </ul>
      </div>
    </div>
  );
}

function Kpi({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="card">
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-500">{label}</div>
      <div className="mt-2 text-3xl font-bold">{value}</div>
      {hint && <div className="mt-1 text-xs font-semibold text-brand-500">{hint}</div>}
    </div>
  );
}

// Petite liste « barres » : proportionne chaque ligne au plus grand total,
// sans dépendance externe (recharts est réservé au graphe temporel).
function BarList({
  items,
  empty,
}: {
  items: Array<{ label: string; value: number }>;
  empty: string;
}) {
  if (items.length === 0) {
    return <p className="py-6 text-center text-sm text-ink-500">{empty}</p>;
  }
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i.label}>
          <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="truncate text-ink-700" title={i.label}>
              {i.label}
            </span>
            <span className="shrink-0 font-bold tabular-nums">{i.value}</span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded bg-ink-100">
            <div
              className="h-full rounded bg-brand-500"
              style={{ width: `${(i.value / max) * 100}%` }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

// Regroupe les lignes (bucket, type) renvoyées par l'API en points de graphe
// { day, pageview, lead, reservation_click }. Le libellé de l'axe X dépend du
// pas : jour/semaine → « JJ/MM », mois → « MM/AA ».
function aggregateByDay(
  events: Array<{ day: string; type: string; count: number }>,
  granularity: Granularity,
) {
  const map = new Map<string, Record<string, number>>();
  for (const e of events) {
    const key = new Date(e.day).toISOString().slice(0, 10);
    if (!map.has(key)) map.set(key, {});
    const entry = map.get(key)!;
    entry[e.type] = (entry[e.type] ?? 0) + e.count;
  }
  const fmt = (iso: string) => {
    const [y = "", m = "", d = ""] = iso.split("-");
    return granularity === "month" ? `${m}/${y.slice(2)}` : `${d}/${m}`;
  };
  return [...map.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, counts]): Record<string, string | number> => ({ ...counts, day: fmt(day) }));
}
